import type { HalfEdgeWallOffsetsCache } from "building-generation/generationTools/generateRoomOutlinesTools";
import { polyLine2RoomOutlineByWallLayer } from "building-generation/generationTools/generateRoomOutlinesTools";
import type { Building } from "building-types/building/buildingTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import {
  RoomType,
  RoomLabelDimensionType,
  RoomTagDimensionControl,
  defaultRoomTagDimensionControl,
} from "building-types/rooms/roomTypesEnum";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { levelInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import type { VisitedRoomsTable } from "doc/plans/interiorLabelObstacle";
import { isValidRoomSet } from "doc/plans/interiorLabelObstacle";
import { aabb2Area, aabb2ByPolyLine2s } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import { offsetPolyLine2 } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphTraceFaceGroupPerimeter } from "geom/planarGraph";
import { polygonSubtractSimple } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2FromAABB2,
  polyLine2MergeCoincidentVertices,
  polyLine2MergeCollinearEdges,
  polyLine2Rotate,
} from "geom/polyLine2";
import { getWidthAndHeightForAABB2AndRotationAngle, polyLine2MaximalRectangle } from "geom/polyLine2MaximalRect";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type { RoomLabelFormatter, RoomLabelFormatterSettings } from "./roomLabelTypes";

/** 20 square feet = 20 x 144 sq in per sq ft */
// Re-export from shared types for backwards compatibility
export { ROOM_AREA_THRESHOLD_TO_SWITCH_TO_SHORT_NAME } from "./roomLabelTypes";
const ALLOWABLE_DIFF_BETWEEN_PL_AND_BOUNDING_BOX = 144 * 10;
const MAX_PERCENTAGE_DIFF_BETWEEN_PL_AND_BOUNDING_BOX = 0.3;
const MIN_CONNECTING_EDGE_LENGTH_PERCENTAGE_FOR_LATE_DECOMP_COMBINATIONS = 0.6;
const DECOMP_OFFSET_DIST_FOR_JOIN = 0.1;
const ADJACENT_DECOMP_EDGE_DIST = 0.001;
const DECOMP_OFFSET_DIST_EPSILON = DECOMP_OFFSET_DIST_FOR_JOIN + ADJACENT_DECOMP_EDGE_DIST;
const DECOMP_MINIMUM_DIM_RECURSION_LIMIT = 24;

export interface RoomDecomposition {
  /** helpful in tracking parent child relationships */
  readonly id: string;
  /** a parent is a larger rectangular polygon that is carved out of the room polygon during room decomposition */
  readonly parentId?: string;
  /** name of room or representative room when more than one room */
  readonly name: string;
  /** id of room or representative room when more than one room */
  readonly roomId?: string;
  /** hide room dims, often due to impracticability of representing the shape with simple width and height measures */
  readonly hideDims: boolean;
  /** room decomposition result */
  readonly pl: PolyLine2;
  /** area of room decomposition cached for re-use */
  readonly area: number;
  /** room table ids included in room decomposition */
  readonly roomIds: string[];
  /** width in rounded feet (Internal Use Only) */
  readonly width: number;
  /** height in rounded feet (Internal Use Only) */
  readonly height: number;
  /** width in inches. */
  readonly presentationalWidth: LevelInches;
  /** height in inches. */
  readonly presentationalHeight: LevelInches;

  readonly angleToVertical: number;
  readonly manualOverride?: boolean;
  readonly widthOverride?: string;
  readonly heightOverride?: string;
}

interface RoomDecompositionAndIds {
  readonly pl: PolyLine2;
  readonly roomIds: string[];
}

interface MakeRoomNameAndDimLabelsByRoomDecompositionOptions {
  readonly roomSets: RoomSet[];
  readonly rooms: Table<Room>;
  readonly levelRoomIds: string[];
  readonly polygonPlanarGraph: PolygonPlanarGraph;
  readonly roomLevelIndices: Table<number>;
  readonly planarGraph: PlanarGraph;
  readonly wallMassSystem: WallMassSystem;
  readonly roomWidthAndHeightRequirements?: (width: number, height: number) => boolean;
  /** affects room combining and filtering of rooms will receive a label */
  readonly formatRoomNameLabel: RoomLabelFormatter;
  readonly floorPlanSettings: RoomLabelFormatterSettings;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly floorSystems: Table<FloorSystem>;
}

/**
 * Groups open-connected rooms of similar types for labeling, then opportunistically decomposes
 * non-rectangular polylines into rectangular subpolyLines, if possible, for further labeling.
 * */
export function makeRoomNameAndDimLabelsByRoomDecomposition({
  roomSets,
  rooms,
  levelRoomIds,
  polygonPlanarGraph,
  roomLevelIndices,
  planarGraph,
  wallMassSystem,
  formatRoomNameLabel,
  floorPlanSettings,
  roomFloorAssemblies,
  floorSystems,
  roomWidthAndHeightRequirements = (width: number, height: number) => {
    if (Number.isNaN(width) || Number.isNaN(height)) {
      return false;
    }
    if (width < 48 || height < 48) {
      return false;
    }
    return true;
  },
}: MakeRoomNameAndDimLabelsByRoomDecompositionOptions) {
  const roomDecomps: RoomDecomposition[] = [];
  const skippedRoomDecomps: RoomDecomposition[] = [];
  const visitedRooms: VisitedRoomsTable = {};

  const roomLabelTable: { [roomId: string]: string } = levelRoomIds.reduce((acc, roomId) => {
    acc[roomId] = formatRoomNameLabel(floorPlanSettings, rooms[roomId], roomFloorAssemblies, floorSystems);
    return acc;
  }, {});
  const bypassRoomIdsWithManualOverrides: { [roomId: string]: boolean } = {};
  const manualOverrideRoomDecompositions = getManualOverrideRoomDecompositions(
    levelRoomIds,
    rooms,
    roomLabelTable,
    polygonPlanarGraph,
    wallMassSystem,
    roomLevelIndices,
    bypassRoomIdsWithManualOverrides,
  );

  for (const roomSet of roomSets) {
    // this check should not be required (technical debt) and the core room set function should be fixed instead
    if (!isValidRoomSet(roomSet, rooms, visitedRooms)) {
      continue;
    }

    // collect room ids for room decomposition
    const roomIdsForRoomDecomposition = roomSet.map((x) => x.id).filter((x) => !bypassRoomIdsWithManualOverrides[x]);

    // group room ids and collate planar graph face indices for those room ids
    const planarGraphIndexByRoomNameInPlan = getRoomGroupingPlanarGraphFaceIndicesByRoomAttributes(
      roomIdsForRoomDecomposition,
      rooms,
      polygonPlanarGraph,
      roomLevelIndices,
    );

    for (const roomTypeKey in planarGraphIndexByRoomNameInPlan) {
      const listOfIndexAndAttributes = planarGraphIndexByRoomNameInPlan[roomTypeKey];

      const roomGroupOutlineAndIds: RoomDecompositionAndIds[] = getPolyLinesForPlanarGraphFaceIndices(
        planarGraph,
        wallMassSystem,
        listOfIndexAndAttributes,
      );

      // use largest area room with a valid label as the representative for the whole group
      for (const { pl, roomIds } of roomGroupOutlineAndIds) {
        const roomGroupDecompArr: RoomDecomposition[] = [];
        const skippedRoomGroupDecompArr: RoomDecomposition[] = [];

        for (const roomId of roomIds) {
          roomLabelTable[roomId] =
            roomLabelTable[roomId] ??
            formatRoomNameLabel(floorPlanSettings, rooms[roomId], roomFloorAssemblies, floorSystems);
        }

        const representativeRoomId = getRepresentativeRoomIdForGroup(roomIds, rooms, roomLabelTable);
        const representativeRoomLabel = roomLabelTable[representativeRoomId];

        recursivelyDecomposePolyLine2({
          pl,
          representativeRoomLabel,
          roomIds,
          roomWidthAndHeightRequirements,
          decompArr: roomGroupDecompArr,
          skippedDecompArr: skippedRoomGroupDecompArr,
          representativeRoomId,
        });

        // post processing to late combine multiple decomps from same source room ids, if possible
        tryCombineAdjacentRoomDecompositions(
          roomGroupDecompArr,
          skippedRoomGroupDecompArr,
          roomDecomps,
          skippedRoomDecomps,
        );
      }
    }
  }

  // add manual override array to the room decomposition array
  if (manualOverrideRoomDecompositions?.length) {
    roomDecomps.push(...manualOverrideRoomDecompositions);
  }

  return { roomDecomps, skippedRoomDecomps };
}

interface SkippedWithArrIndex extends RoomDecomposition {
  readonly arrIndex: string;
  readonly edgeLength: number;
}

interface IndexAndRoomAttributes {
  readonly roomId: string;
  readonly planarGraphFaceIndex: number;
}

interface RecursivelyDecomposePolyLine2Options {
  /** the list of decompositions that pass height and width requirements */
  readonly decompArr: RoomDecomposition[];
  /** the list of decompositions that don't pass height and width requirements */
  readonly skippedDecompArr: RoomDecomposition[];
  /** the larger room decomposition that was subtracted from the polyline before this recursion step */
  readonly parentId?: string;
  /** the representative room in group that is used to provide a name label to the room decomposition,
   *  which may be comprised of multiple rooms. The label is a potentially multiline string of the name and tags.
   *  If a drawing template is available, use that instead of the label. */
  readonly representativeRoomLabel: string;
  readonly representativeRoomId?: string;
  /** ids of rooms in group */
  readonly roomIds: string[];
  /** resultant decomp polyLine (usually a rectangle) */
  readonly pl: PolyLine2;
  /** room polygons not fulfilling these requirements do not receive labels */
  readonly roomWidthAndHeightRequirements: (width: number, height: number) => boolean;
  /** the rotation angle passed down from the original largest room decomposition that started the recursion */
  readonly parentLongestEdgeAngle?: number;
  /** when a parent decomp is marked as skipped, all children decomps are also skipped, but we want to collect them, so we keep recursing */
  readonly childrenDecompositionsSkipped?: boolean;
}

/** Get room id of the room that will provide the name for the group of rooms */
function getRepresentativeRoomIdForGroup(
  roomIds: string[],
  rooms: Table<Room>,
  roomLabelTable: { [roomId: string]: string },
): string {
  let largestArea = -1;
  let representativeRoomId: string = roomIds[0];

  if (roomIds.length > 1) {
    // if more then one room, use the largest area room with an appropriate room name as the respresentative room
    for (const roomId of roomIds) {
      const room = rooms[roomId];
      const roomLabel = roomLabelTable[roomId];
      const areaInSqInches = polyLine2Area(room.outline);

      if (roomLabel?.length > 0 && areaInSqInches > largestArea) {
        largestArea = areaInSqInches;
        representativeRoomId = roomId;
      }
    }
  }

  return representativeRoomId;
}

/** get joined polyline for planar graph face indices that comprise the room decomp  */
function getPolyLinesForPlanarGraphFaceIndices(
  planarGraph: PlanarGraph,
  wallMassSystem: WallMassSystem,
  listOfIndexAndAttributes: IndexAndRoomAttributes[],
) {
  const roomIds = listOfIndexAndAttributes.map((x) => x.roomId);
  const indices = listOfIndexAndAttributes.map((x) => x.planarGraphFaceIndex);

  const roomGroupOutlineAndIds: RoomDecompositionAndIds[] = [];
  const wallOffsetCache: HalfEdgeWallOffsetsCache = {};
  const { faceHalfEdges, faceVertices } = planarGraphTraceFaceGroupPerimeter(planarGraph, indices);
  for (let face = 0, faceCount = faceHalfEdges.length; face < faceCount; face++) {
    const pl = polyLine2RoomOutlineByWallLayer({
      wallLayer: WallLayerType.MASS, // TODO: use structure layer?
      faceHalfEdges,
      face,
      wallMassSystem,
      wallOffsetCache,
      planarGraph,
      faceVertices,
      ignoreVoidLayers: true,
    });

    roomGroupOutlineAndIds.push({ pl, roomIds });
  }
  return roomGroupOutlineAndIds;
}

/** Adjacent room decomps can be joined into a single decomp if the group's bounding box passes requirements */
function tryCombineAdjacentRoomDecompositions(
  arr: RoomDecomposition[],
  skippedArr: RoomDecomposition[],
  roomDecompositionArr: RoomDecomposition[],
  skippedDecompArray: RoomDecomposition[],
) {
  // sort largest to smallest by area to preference combining larger decomps first
  arr.sort((a, b) => b.area - a.area);
  skippedArr.sort((a, b) => b.area - a.area);

  // When combining decomps into a single group, a previously added decomp will need to be removed (cancelled) if we ultimately reuse it in a new group.
  const arrCancellations: { [addedIndex: string]: boolean } = {};
  const skippedArrCancellations: { [removedIndex: string]: boolean } = {};

  const lateCombinedRoomDecomps: { [id: string]: RoomDecomposition } = {};

  for (const parentIndex of Object.keys(arr)) {
    const roomDecomp = arr[parentIndex];

    // create a table to map skipped decomps to edges of the larger decomp, so that we can try to join all adjacent skipped decomps on an edge at one time
    const edgeTable: { [edgeIndex: number]: SkippedWithArrIndex[] } = {};

    // loop through all skipped decomps and try to join them to larger decomps
    for (const index of Object.keys(skippedArr)) {
      // if skipped decomp has already been added to another larger decomp, skip it
      if (skippedArrCancellations[index]) {
        continue;
      }

      const skippedRoomDecomp: RoomDecomposition = skippedArr[index];

      for (let i = 0; i < polyLine2EdgeCount(skippedRoomDecomp.pl); i++) {
        // test if middle of edge of smaller skipped decomp is adjacent to a larger decomp
        const [x, y] = polyLine2EdgeEval(skippedRoomDecomp.pl, i, 0.5, true);
        const cp = closestPointPolyLine2(roomDecomp.pl, x, y);
        if (cp?.dist > DECOMP_OFFSET_DIST_EPSILON) {
          // this removed poly doesn't touch
          continue;
        }

        // TODO: only parallel touching edges should be considered adjacent, but this isn't currently checked.

        const edgeLength = polyLine2EdgeLength(skippedRoomDecomp.pl, i);

        const arr = edgeTable[cp.edgeIndex] || [];
        arr.push({
          ...skippedRoomDecomp,
          arrIndex: index,
          edgeLength,
        });

        edgeTable[cp.edgeIndex] = arr;
      }

      for (const edgeIndex in edgeTable) {
        const largerEdgeLength = polyLine2EdgeLength(roomDecomp.pl, +edgeIndex);
        const group = edgeTable[edgeIndex];

        let totalLengthOfConnection = 0;
        let totalArea = 0;

        const pls: PolyLine2[] = [];
        const roomIds: string[] = [];
        const indicesToPossiblyReplace: string[] = [];

        const angleToVertical = roomDecomp.angleToVertical;
        for (const subPoly of group) {
          totalLengthOfConnection += subPoly.edgeLength;
          totalArea += subPoly.area;
          pls.push(
            Math.abs(angleToVertical) > ANGLE_EPSILON
              ? polyLine2Rotate(polyLine2Copy(subPoly.pl), angleToVertical)
              : subPoly.pl,
          );
          roomIds.push(...subPoly.roomIds);
          indicesToPossiblyReplace.push(subPoly.arrIndex);
        }

        if (
          totalLengthOfConnection / largerEdgeLength <
          MIN_CONNECTING_EDGE_LENGTH_PERCENTAGE_FOR_LATE_DECOMP_COMBINATIONS
        ) {
          continue;
        }

        const joinedArea = totalArea + roomDecomp.area;
        const joinedAABB = aabb2ByPolyLine2s([
          ...pls,
          Math.abs(angleToVertical) > ANGLE_EPSILON
            ? polyLine2Rotate(polyLine2Copy(roomDecomp.pl), angleToVertical)
            : roomDecomp.pl,
        ]);
        const joinedAABBArea = joinedAABB ? aabb2Area(joinedAABB) : 0;
        const diffJoinedAABBToPl = joinedAABBArea - joinedArea;

        if (diffJoinedAABBToPl <= ALLOWABLE_DIFF_BETWEEN_PL_AND_BOUNDING_BOX) {
          for (const cancelIndex of indicesToPossiblyReplace) {
            skippedArrCancellations[cancelIndex] = true;
          }
          arrCancellations[parentIndex] = true;
          const aabb = aabb2ByPolyLine2s([
            ...pls,
            Math.abs(angleToVertical) > ANGLE_EPSILON
              ? polyLine2Rotate(polyLine2Copy(roomDecomp.pl), angleToVertical)
              : roomDecomp.pl,
          ]);
          if (aabb) {
            const { width, height } = getWidthAndHeightForAABB2AndRotationAngle(angleToVertical, aabb);
            const { roundedWidth, roundedHeight } = feetToInchWithNonStandardRounding(width, height);
            const outline = polyLine2Rotate(polyLine2FromAABB2(aabb), -angleToVertical);

            if (lateCombinedRoomDecomps[roomDecomp.id]) {
              continue;
            }

            lateCombinedRoomDecomps[roomDecomp.id] = {
              id: roomDecomp.id,
              parentId: roomDecomp.parentId,
              width: roundedWidth,
              height: roundedHeight,
              presentationalHeight: levelInchesNew(width),
              presentationalWidth: levelInchesNew(height),
              name: roomDecomp.name,
              hideDims: roomDecomp.hideDims,
              pl: outline,
              roomIds,
              area: width * height,
              angleToVertical,
            };
          }
        }
      }
    }
  }

  for (const key of Object.keys(arr)) {
    if (arrCancellations[key]) {
      continue;
    }
    roomDecompositionArr.push(arr[key]);
  }

  roomDecompositionArr.push(...Object.values(lateCombinedRoomDecomps));

  for (const key of Object.keys(skippedArr)) {
    if (skippedArrCancellations[key]) {
      continue;
    }
    if (skippedDecompArray) {
      skippedDecompArray.push(skippedArr[key]);
    }
  }
}

// This function is only exported for testing purposes.
export function testOnlyRecursivelyDecomposePolyLine2(props: RecursivelyDecomposePolyLine2Options) {
  recursivelyDecomposePolyLine2(props);
}

const CELL_NUMBER_LIMIT = 150 * 150;
/** Break larger polyline into smaller rectangular decompositions. Collect skipped polylines (too small or non-rectangular) for analytics. */
function recursivelyDecomposePolyLine2({
  pl,
  representativeRoomLabel,
  roomIds,
  roomWidthAndHeightRequirements,
  decompArr,
  skippedDecompArr,
  parentId,
  childrenDecompositionsSkipped = false,
  parentLongestEdgeAngle = undefined,
  representativeRoomId,
}: RecursivelyDecomposePolyLine2Options) {
  const id = uid();
  const {
    maxRectAsPolyLine2: maxRect,
    width,
    height,
    aabbWidth,
    aabbHeight,
    aabbAsPolyLine2: aabbAsPl,
    maxRectFailed,
    isAlreadyRectangle,
    angleToVertical,
  } = polyLine2MaximalRectangle(pl, parentLongestEdgeAngle, CELL_NUMBER_LIMIT);

  const aabbArea = polyLine2Area(aabbAsPl);
  const plArea = polyLine2Area(pl);
  const diffAABBToPl = aabbArea - plArea;
  const diffAABBToPlPercentageOfArea = (aabbArea - plArea) / aabbArea;

  let resolvedWidth = 0;
  let resolvedHeight = 0;
  let resolvedPl: PolyLine2;
  let hideDims = false;

  if (
    isAlreadyRectangle ||
    (diffAABBToPl <= ALLOWABLE_DIFF_BETWEEN_PL_AND_BOUNDING_BOX &&
      diffAABBToPlPercentageOfArea < MAX_PERCENTAGE_DIFF_BETWEEN_PL_AND_BOUNDING_BOX)
  ) {
    if (!roomWidthAndHeightRequirements(aabbWidth, aabbHeight)) {
      if (skippedDecompArr) {
        if (
          Number.isNaN(aabbWidth) ||
          Number.isNaN(aabbHeight) ||
          aabbWidth < DIST_EPSILON ||
          aabbHeight < DIST_EPSILON
        ) {
          skippedDecompArr.push({
            id,
            parentId,
            width: 0,
            height: 0,
            name: representativeRoomLabel,
            hideDims: true,
            pl,
            roomIds,
            area: 0,
            presentationalHeight: levelInchesNew(0),
            presentationalWidth: levelInchesNew(0),
            angleToVertical: 0,
            roomId: representativeRoomId,
          });

          return;
        }

        const { roundedWidth, roundedHeight } = feetToInchWithNonStandardRounding(aabbWidth, aabbHeight);
        skippedDecompArr.push({
          id,
          parentId,
          width: roundedWidth,
          height: roundedHeight,
          presentationalWidth: levelInchesNew(aabbWidth),
          presentationalHeight: levelInchesNew(aabbHeight),
          name: representativeRoomLabel,
          hideDims: false,
          pl,
          roomIds,
          area: plArea,
          angleToVertical,
          roomId: representativeRoomId,
        });
      }
      return;
    }

    resolvedPl = aabbAsPl;
    resolvedWidth = aabbWidth;
    resolvedHeight = aabbHeight;
  } else {
    if (maxRectFailed) {
      console.warn("This should not happen! Max rect algorithm failed in an unforeseen way.");
      hideDims = true;
      resolvedPl = aabbAsPl;
    } else {
      resolvedWidth = width;
      resolvedHeight = height;
      resolvedPl = maxRect;

      if (skippedDecompArr) {
        if (
          Number.isNaN(aabbWidth) ||
          Number.isNaN(aabbHeight) ||
          aabbWidth < DIST_EPSILON ||
          aabbHeight < DIST_EPSILON
        ) {
          skippedDecompArr.push({
            id,
            parentId,
            width: 0,
            height: 0,
            presentationalHeight: levelInchesNew(0),
            presentationalWidth: levelInchesNew(0),
            name: representativeRoomLabel,
            hideDims: true,
            pl,
            roomIds,
            area: 0,
            angleToVertical: 0,
            roomId: representativeRoomId,
          });
          return;
        }
      }

      if (Math.min(width, height) > DECOMP_MINIMUM_DIM_RECURSION_LIMIT) {
        const subPolyLine2sWithConnectingEdgeInfo = getPolyLine2DifferencePolyline2sWithConnectingEdgeInfo(pl, maxRect);

        const subPolyEdgeGroups: { [edgeId: number]: SubPolyLine2WithConnectingEdgeInfo[] } = {};
        for (const subPoly of subPolyLine2sWithConnectingEdgeInfo) {
          const group = subPolyEdgeGroups[subPoly.longestConnectingEdgeIndex] || [];
          group.push(subPoly);
          subPolyEdgeGroups[subPoly.longestConnectingEdgeIndex] = group;
        }

        for (const edgeIndex in subPolyEdgeGroups) {
          const group = subPolyEdgeGroups[edgeIndex];
          for (const subPoly of group) {
            recursivelyDecomposePolyLine2({
              pl: subPoly.polyLine2,
              representativeRoomLabel: "", // child decompositions do not re-state the representative room name
              roomIds,
              roomWidthAndHeightRequirements,
              decompArr,
              skippedDecompArr,
              parentId: id,
              childrenDecompositionsSkipped,
              parentLongestEdgeAngle: parentLongestEdgeAngle !== undefined ? parentLongestEdgeAngle : angleToVertical,
            });
          }
        }
      }

      if (!roomWidthAndHeightRequirements(width, height)) {
        if (skippedDecompArr) {
          if (Number.isNaN(width) || Number.isNaN(height)) {
            skippedDecompArr.push({
              id,
              parentId,
              width: 0,
              height: 0,
              presentationalHeight: levelInchesNew(0),
              presentationalWidth: levelInchesNew(0),
              name: representativeRoomLabel,
              hideDims: true,
              pl,
              roomIds,
              area: 0,
              angleToVertical: 0,
              roomId: representativeRoomId,
            });
            return;
          }
          const { roundedWidth, roundedHeight } = feetToInchWithNonStandardRounding(width, height);
          skippedDecompArr.push({
            id,
            parentId,
            width: roundedWidth,
            height: roundedHeight,
            presentationalWidth: levelInchesNew(height),
            presentationalHeight: levelInchesNew(width),
            name: representativeRoomLabel,
            hideDims: false,
            pl: maxRect,
            roomIds,
            area: width * height,
            angleToVertical,
            roomId: representativeRoomId,
          });
        }
        return;
      }
    }
  }

  if (childrenDecompositionsSkipped) {
    if (skippedDecompArr) {
      if (Number.isNaN(resolvedWidth) || Number.isNaN(resolvedHeight)) {
        skippedDecompArr.push({
          id,
          parentId,
          width: 0,
          height: 0,
          presentationalHeight: levelInchesNew(0),
          presentationalWidth: levelInchesNew(0),
          name: representativeRoomLabel,
          hideDims: true,
          pl,
          roomIds,
          area: 0,
          angleToVertical: 0,
          roomId: representativeRoomId,
        });
        return;
      }
      const { roundedWidth, roundedHeight } = feetToInchWithNonStandardRounding(resolvedWidth, resolvedHeight);
      skippedDecompArr.push({
        id,
        parentId,
        width: roundedWidth,
        height: roundedHeight,
        presentationalWidth: levelInchesNew(resolvedWidth),
        presentationalHeight: levelInchesNew(resolvedHeight),
        name: representativeRoomLabel,
        hideDims,
        pl: resolvedPl,
        roomIds,
        area: resolvedHeight * resolvedWidth,
        angleToVertical,
        roomId: representativeRoomId,
      });
    }
  } else {
    const { roundedWidth, roundedHeight } = feetToInchWithNonStandardRounding(resolvedWidth, resolvedHeight);
    decompArr.push({
      id,
      parentId,
      width: roundedWidth,
      height: roundedHeight,
      presentationalWidth: levelInchesNew(resolvedWidth),
      presentationalHeight: levelInchesNew(resolvedHeight),
      name: representativeRoomLabel,
      hideDims,
      pl: resolvedPl,
      roomIds,
      area: resolvedHeight * resolvedWidth,
      angleToVertical,
      roomId: representativeRoomId,
    });
  }
}

function feetToInchWithNonStandardRounding(finalWidth: number, finalHeight: number) {
  const widthUnrounded = finalWidth / 12;
  const heightUnrounded = finalHeight / 12;
  const widthFloor = Math.floor(finalWidth / 12);
  const heightFloor = Math.floor(finalHeight / 12);

  // use .75 (9 inch rounds up) instead of floor or .5 rounding
  const roundedWidth = widthUnrounded - widthFloor > 0.75 ? Math.ceil(widthUnrounded) : widthFloor;
  const roundedHeight = heightUnrounded - heightFloor > 0.75 ? Math.ceil(heightUnrounded) : heightFloor;
  return { roundedWidth, roundedHeight };
}

/**
 * Creates a group of rooms based on room attributes (type and name) along with the room's planar graph face index.
 */
function getRoomGroupingPlanarGraphFaceIndicesByRoomAttributes(
  roomIds: string[],
  rooms: Table<Room>,
  polygonPlanarGraph: PolygonPlanarGraph,
  roomLevelIndices: Table<number>,
): { [roomType: string]: IndexAndRoomAttributes[] } {
  const planarGraphIndexByRoomNameInPlan: { [roomType: string]: IndexAndRoomAttributes[] } = {};
  let garageTypeCounter = 0;
  for (const id of roomIds) {
    const room = rooms[id];
    const type = room.type;

    if (room?.attributes?.roomSalesLabelHiddenInBrochure) {
      continue;
    }

    const { outline } = rooms[id];
    if (!outline) {
      continue;
    }

    let roomType: string = type;
    if (type === RoomType.GARAGE || type === RoomType.GARAGE_DETACH) {
      roomType = roomType + garageTypeCounter.toString(); //unique hash so that garage rooms don't join together for room labels
      garageTypeCounter++;
    }

    const listOfIndexAndAttributes: IndexAndRoomAttributes[] = planarGraphIndexByRoomNameInPlan[roomType] || [];
    const index = polygonPlanarGraph.polygonToPlanarGraph[roomLevelIndices[id]]?.[0]?.[0]?.index;
    listOfIndexAndAttributes.push({
      roomId: id,
      planarGraphFaceIndex: index,
    });
    planarGraphIndexByRoomNameInPlan[roomType] = listOfIndexAndAttributes;
  }

  return planarGraphIndexByRoomNameInPlan;
}

function getManualOverrideRoomDecompositions(
  roomIds: string[],
  rooms: Table<Room>,
  roomLabelTable: { [roomId: string]: string },
  polygonPlanarGraph: PolygonPlanarGraph,
  wallMassSystem: WallMassSystem,
  roomLevelIndices: Table<number>,
  bypassRooms: { [roomId: string]: boolean },
): RoomDecomposition[] {
  const roomDecompositionArr: RoomDecomposition[] = [];

  for (const roomId of roomIds) {
    const room = rooms[roomId];

    if (!room?.attributes) {
      continue;
    }

    const attributes = room.attributes;

    if (attributes.roomSalesLabelHiddenInBrochure) {
      continue;
    }

    const { outline } = rooms[roomId];
    if (!outline) {
      continue;
    }

    // defaults to Room Decomposition, so we can safely bypass this room
    if (!attributes.labelDimensionType || attributes.labelDimensionType === RoomLabelDimensionType.ROOM_DECOMPOSITION) {
      continue;
    }

    let widthOverride: string | undefined;
    let heightOverride: string | undefined;
    let hideDims = false;
    let getBoundingBox = false;

    // let's handle the remaining manual override rooms
    switch (attributes.labelDimensionType) {
      case RoomLabelDimensionType.NONE: {
        bypassRooms[roomId] = true;
        hideDims = true;
        break;
      }
      case RoomLabelDimensionType.OVERRIDE: {
        bypassRooms[roomId] = true;
        widthOverride = attributes.labelWidthOverride ?? "";
        heightOverride = attributes.labelHeightOverride ?? "";
        break;
      }
      case RoomLabelDimensionType.BOUNDING_BOX: {
        bypassRooms[roomId] = true;
        getBoundingBox = true;
        break;
      }

      default:
        break;
    }

    const index = polygonPlanarGraph.polygonToPlanarGraph[roomLevelIndices[roomId]]?.[0]?.[0]?.index;
    const listOfIndexAndAttributes: IndexAndRoomAttributes[] = [
      {
        roomId: roomId,
        planarGraphFaceIndex: index,
      },
    ];
    const roomGroupOutlineAndIds: RoomDecompositionAndIds[] = getPolyLinesForPlanarGraphFaceIndices(
      polygonPlanarGraph.planarGraph,
      wallMassSystem,
      listOfIndexAndAttributes,
    );

    const pls = roomGroupOutlineAndIds.map((x) => x.pl);

    for (let pl of pls) {
      let width: number = 0;
      let height: number = 0;
      let presentationalWidth: LevelInches = levelInchesNew(0);
      let presentationalHeight: LevelInches = levelInchesNew(0);
      let area = 0;

      if (getBoundingBox) {
        const aabb = polyLine2AABB2(pl);
        const w = aabb2Width(aabb);
        const h = aabb2Height(aabb);
        presentationalWidth = levelInchesNew(w);
        presentationalHeight = levelInchesNew(h);
        const { roundedWidth, roundedHeight } = feetToInchWithNonStandardRounding(w, h);
        area = w * h;
        width = roundedWidth;
        height = roundedHeight;
        pl = polyLine2FromAABB2(aabb);
      }

      roomDecompositionArr.push({
        id: uid(),
        parentId: undefined,
        width: width,
        height: height,
        presentationalWidth,
        presentationalHeight,
        name: roomLabelTable[roomId],
        hideDims,
        pl,
        roomIds: [roomId],
        area,
        angleToVertical: 0,
        manualOverride: true,
        widthOverride,
        heightOverride,
        roomId,
      });
    }
  }

  return roomDecompositionArr;
}

interface SubPolyLine2WithConnectingEdgeInfo {
  readonly polyLine2: PolyLine2;
  readonly connectingEdgeLengthTotal: number;
  readonly longestConnectingEdgeIndex: number;
  readonly longestConnectingEdgeLengthOfLargerPl: number;
  readonly area: number;
}

function getPolyLine2DifferencePolyline2sWithConnectingEdgeInfo(
  largerPoly: PolyLine2,
  smallerPoly: PolyLine2,
): SubPolyLine2WithConnectingEdgeInfo[] {
  const subPls: SubPolyLine2WithConnectingEdgeInfo[] = [];

  /** slightly increase maxRect size so that polygon subtract is consistent */
  const maxRect = offsetPolyLine2(polyLine2Copy(smallerPoly), DECOMP_OFFSET_DIST_FOR_JOIN);
  const differencePolys: PolyLine2Polygon[] = [];

  /** if this fails we won't push array, so it's okay to fail silently */
  try {
    differencePolys.push(...polygonSubtractSimple(largerPoly, maxRect));
  } catch {
    console.warn(
      "Polygon subtraction failed for room decomposition. Non-critical error (It's likely the shape can't be decomposed further).",
    );
  }

  for (const polygon of differencePolys) {
    let polygonAppendage = polygon[0];

    polygonAppendage = polyLine2MergeCoincidentVertices(polygonAppendage);
    polygonAppendage = polyLine2MergeCollinearEdges(polygonAppendage);

    let connectingEdgeLengthTotal: number | undefined;
    let longestConnectingEdgeIndex: number | undefined;
    let longestConnectingEdgeLength = 0;
    let longestConnectingEdgeLengthOfLargerPl = 0;

    for (let i = 0; i < polyLine2EdgeCount(polygonAppendage); i++) {
      const [x, y] = polyLine2EdgeEval(polygonAppendage, i, 0.5, true);
      const cpMaxRect = closestPointPolyLine2(maxRect, x, y);

      if (cpMaxRect?.dist < ADJACENT_DECOMP_EDGE_DIST) {
        const connectingEdgeLength = polyLine2EdgeLength(polygonAppendage, i);
        if (connectingEdgeLength > longestConnectingEdgeLength) {
          longestConnectingEdgeLength = connectingEdgeLength;
          connectingEdgeLengthTotal = connectingEdgeLength;
          longestConnectingEdgeIndex = cpMaxRect.edgeIndex;
          longestConnectingEdgeLengthOfLargerPl = polyLine2EdgeLength(maxRect, cpMaxRect.edgeIndex);
        }
      }
    }

    if (longestConnectingEdgeIndex === undefined || connectingEdgeLengthTotal === undefined) {
      continue;
    }

    subPls.push({
      polyLine2: polygonAppendage,
      connectingEdgeLengthTotal,
      area: polyLine2Area(polygonAppendage),
      longestConnectingEdgeIndex,
      longestConnectingEdgeLengthOfLargerPl,
    });
  }

  return subPls.sort((a, b) => b.area - a.area);
}

interface GetRoomDecompAndIdsForSingleRoomProps {
  readonly room: Room;
  readonly rooms: Table<Room>;
  readonly polygonPlanarGraph: PolygonPlanarGraph;
  readonly roomLevelIndices: Table<number>;
  readonly planarGraph: PlanarGraph;
  readonly wallMassSystem: WallMassSystem;
}

function getRoomOutlines({
  room,
  rooms,
  polygonPlanarGraph,
  roomLevelIndices,
  planarGraph,
  wallMassSystem,
}: GetRoomDecompAndIdsForSingleRoomProps): RoomDecompositionAndIds[] {
  const roomIdsForRoomDecomposition = [room.id];

  const planarGraphIndexByRoomNameInPlan = getRoomGroupingPlanarGraphFaceIndicesByRoomAttributes(
    roomIdsForRoomDecomposition,
    rooms,
    polygonPlanarGraph,
    roomLevelIndices,
  );

  const planarGraphValues = Object.values(planarGraphIndexByRoomNameInPlan);
  // Since we pass a single room ID, we expect exactly one group entry.
  // If the room was filtered out (zero) or unexpectedly split (multiple), fall back to the default room outline.
  if (planarGraphValues.length !== 1) {
    return [];
  }

  const listOfIndexAndAttributes = planarGraphValues[0];

  const roomGroupOutlineAndIds: RoomDecompositionAndIds[] = getPolyLinesForPlanarGraphFaceIndices(
    planarGraph,
    wallMassSystem,
    listOfIndexAndAttributes,
  );

  return roomGroupOutlineAndIds;
}

export interface RoomDimensions {
  width: number;
  height: number;
  area: number;
}

export function computeInaccessibilityPolygonRoomDimensions(
  room: Room,
  rooms: Table<Room>,
  building: Building,
  levelId: string,
): RoomDimensions {
  const roomOutlines = getRoomOutlines({
    room,
    rooms,
    planarGraph: building.planarGraphs[levelId].planarGraph,
    polygonPlanarGraph: building.planarGraphs[levelId],
    wallMassSystem: building.wallMasses[levelId],
    roomLevelIndices: building.roomLevelIndices,
  });
  /**
   * If the room outline polyline is decomposed by wall layer into zero or multiple polylines,
   * fall back to the original room outline for the dimension calculations.
   **/
  const roomOutlinePolyLine = roomOutlines.length === 1 ? roomOutlines[0].pl : room.outline;
  const control = room.tagDimensionControl ?? defaultRoomTagDimensionControl;
  const plArea = polyLine2Area(roomOutlinePolyLine);

  if (control === RoomTagDimensionControl.AXIS_ALIGNED_BOUNDING_BOX) {
    const boundingBox = polyLine2AABB2(roomOutlinePolyLine);
    return { width: aabb2Width(boundingBox), height: aabb2Height(boundingBox), area: plArea };
  }

  const { width, height } = polyLine2MaximalRectangle(roomOutlinePolyLine, undefined, CELL_NUMBER_LIMIT);

  return { width, height, area: plArea };
}
